import { useSession } from "@clerk/clerk-react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { Check, Spinner, WarningCircle } from "@phosphor-icons/react";
import { AlertCircle } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { RentalContract } from "shared/types/rental-contracts";

export interface TerminateContractAbruptModalProps {
    isOpen: boolean;
    onClose?: () => void;
    incomingContract: RentalContract | null;
    handleContractRefresh: () => void;
}

type TerminationState = 'idle' | 'loading' | 'success' | 'error';

export const TerminateContractAbruptModal = ({ isOpen, onClose, incomingContract, handleContractRefresh }: TerminateContractAbruptModalProps) => {
    const [selectedContract, setSelectedContract] = useState<RentalContract | null>(null);
    const [terminationState, setTerminationState] = useState<TerminationState>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { session } = useSession();

    useEffect(() => {
        setSelectedContract(incomingContract);
        if (incomingContract) {
            setTerminationState('idle');
            setErrorMessage(null);
        }
    }, [incomingContract]);

    const handleClose = () => {
        if (terminationState === 'loading') return;

        setSelectedContract(null);
        setTerminationState('idle');
        setErrorMessage(null);
        onClose && onClose();
    };

    const terminateContract = async () => {
        if (!selectedContract?.id) return;

        setTerminationState('loading');
        setErrorMessage(null);

        try {
            await fetchWithAuth(`${API_URL}/rental/contract/${selectedContract.id}/terminate/abrupt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            }, session);

            setTerminationState('success');
            toast.success('Contract succesvol beëindigd! Toekomstige facturatie is gestopt.');

            setTimeout(() => {
                handleContractRefresh();
                setSelectedContract(null);
                setTerminationState('idle');
                setErrorMessage(null);
                onClose && onClose();
            }, 1000);

        } catch (error) {
            const message = error instanceof Error ? error.message : 'Onbekende fout opgetreden';
            setTerminationState('error');
            setErrorMessage(message);
            toast.error('Fout bij het beëindigen van het contract. Probeer opnieuw.');
        }
    };

    const isLoading = terminationState === 'loading';
    const isSuccess = terminationState === 'success';
    const isError = terminationState === 'error';

    return (
        <Modal
            onClose={handleClose}
            isOpen={isOpen}
            isDismissable={!isLoading}
            isKeyboardDismissDisabled={isLoading}
            className="font-normal text-neutral-primary"
            size="2xl"
            scrollBehavior="inside"
        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                                <AlertCircle className="h-[17px] w-[17px]" /> Beëindig contract
                            </p>
                        </ModalHeader>

                        <ModalBody>
                            {terminationState === 'idle' && (
                                <p className="text-neutral-primary">
                                    Ben je zeker dat je dit contract nu meteen wil beëindigen?
                                </p>
                            )}

                            {isLoading && (
                                <div className="flex items-center gap-3 py-2">
                                    <Spinner className="h-5 w-5 text-neutral-primary animate-spin shrink-0" />
                                    <p className="text-sm text-neutral-primary">
                                        Contract wordt beëindigd...
                                    </p>
                                </div>
                            )}

                            {isSuccess && (
                                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <Check className="h-5 w-5 text-green-600 shrink-0" />
                                    <p className="text-sm text-green-800 font-medium">
                                        Contract succesvol beëindigd
                                    </p>
                                </div>
                            )}

                            {isError && (
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <WarningCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-red-800">
                                                Er is een fout opgetreden bij het beëindigen
                                            </p>
                                            {errorMessage && (
                                                <p className="text-xs text-red-600 mt-1">
                                                    {errorMessage}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-neutral-secondary-mid">
                                        Het contract is niet beëindigd. Probeer opnieuw of neem contact op met de beheerder.
                                    </p>
                                </div>
                            )}
                        </ModalBody>

                        <ModalFooter className="justify-start">
                            {terminationState === 'idle' && (
                                <>
                                    <ButtonNew
                                        variant="primaryBrick"
                                        onClick={terminateContract}
                                        icon={<Check />}
                                        label="Beëindig contract"
                                    />
                                    <ButtonNew
                                        variant="secondaryNeutral"
                                        onClick={onClose}
                                        label="Annuleer"
                                    />
                                </>
                            )}

                            {isLoading && (
                                <ButtonNew
                                    variant="secondaryNeutral"
                                    isDisabled={true}
                                    icon={<Spinner className="animate-spin" />}
                                    label="Contract wordt beëindigd..."
                                />
                            )}

                            {isSuccess && (
                                <ButtonNew
                                    variant="primaryStatusGreen"
                                    isDisabled={true}
                                    icon={<Check />}
                                    label="Contract beëindigd"
                                />
                            )}

                            {isError && (
                                <>
                                    <ButtonNew
                                        variant="primaryBrick"
                                        onClick={terminateContract}
                                        icon={<Check />}
                                        label="Opnieuw proberen"
                                    />
                                    <ButtonNew
                                        variant="secondaryNeutral"
                                        onClick={onClose}
                                        label="Annuleer"
                                    />
                                </>
                            )}
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};
